import { Component, inject, signal, computed, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

import { ZoneMasterService, ZoneMaster, ZoneMasterDto } from '../../services/zone-master';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';

@Component({
  selector: 'app-zones',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DialogModule,
    TableModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    ToggleSwitchModule,
  ],
  templateUrl: './zones.html',
  styleUrls: ['./zones.css'],
})
export class ZonesPage implements OnInit {
  private fb = inject(FormBuilder);
  private svc = inject(ZoneMasterService);
  private toast = inject(ToastService);
  readonly pageTitle = inject(PageTitleService).title;
  private platformId = inject(PLATFORM_ID);

  loading = signal(false);
  saving = signal(false);
  editing = signal(false);
  showModal = signal(false);
  search = signal('');
  rows = signal<ZoneMaster[]>([]);

  filtered = computed(() => {
    const q = this.search().trim().toLowerCase();
    if (!q) return this.rows();
    return this.rows().filter((r) => (r.zoneName || '').toLowerCase().includes(q));
  });

  form: FormGroup = this.fb.group({
    id: [0],
    zoneName: ['', Validators.required],
    isActive: [true],
  });

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) this.load();
  }

  load() {
    this.loading.set(true);
    this.svc.getAll().subscribe({
      next: (res: any) => {
        this.loading.set(false);
        const data = res?.data ?? res ?? [];
        this.rows.set(Array.isArray(data) ? data : []);
      },
      error: () => this.loading.set(false),
    });
  }

  openAdd() {
    this.editing.set(false);
    this.form.reset({ id: 0, zoneName: '', isActive: true });
    this.showModal.set(true);
  }

  openEdit(r: ZoneMaster) {
    this.editing.set(true);
    this.form.reset({ id: r.id, zoneName: r.zoneName ?? '', isActive: r.isActive ?? true });
    this.showModal.set(true);
  }

  closeModal() {
    this.showModal.set(false);
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toast.warning('Zone name is required.');
      return;
    }
    const v = this.form.getRawValue();
    const dto: ZoneMasterDto = {
      id: this.editing() ? Number(v.id) : 0,
      zoneName: (v.zoneName || '').trim(),
      isActive: !!v.isActive,
    };
    this.saving.set(true);
    const req = this.editing() ? this.svc.update(dto) : this.svc.insert(dto);
    req.subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to save zone');
          return;
        }
        this.toast.success(this.editing() ? 'Zone updated' : 'Zone created');
        this.closeModal();
        this.load();
      },
      error: () => this.saving.set(false),
    });
  }

  toggleActive(r: ZoneMaster) {
    const next = !r.isActive;
    this.patchRow(r.id, { isActive: next });
    this.svc.setActive(r.id, next).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.patchRow(r.id, { isActive: r.isActive });
          this.toast.error(res?.message || 'Could not update status.');
        }
      },
      error: () => {
        this.patchRow(r.id, { isActive: r.isActive });
        this.toast.error('Could not update status.');
      },
    });
  }

  private patchRow(id: number, patch: Partial<ZoneMaster>) {
    this.rows.update((list) => list.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  }

  async remove(r: ZoneMaster) {
    const ok = await this.toast.confirm(`"${r.zoneName}" will be deleted.`, 'Delete zone?', 'Delete');
    if (!ok) return;
    this.svc.delete(r.id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to delete');
          return;
        }
        this.toast.success('Zone deleted');
        this.load();
      },
    });
  }
}
